import { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ezyvetClinicsQueryKey, fetchEzyvetClinics, saveEzyvetClinics } from "@/common/lib/api/bridge";
import { useAccountContext } from "@providers/AccountProvider";
import ErrorBox from "@/common/components/ErrorBox";
import CheckCircleIcon from "@icons/check-circle.svg?react";

/** Duck-typed on `.response`, like `EzyvetConnection`: a real ky HTTPError has this shape. */
const errorResponse = (err: unknown) =>
    (err as { response?: { status?: number; json: () => Promise<{ error?: string }> } })?.response;

/**
 * EzyvetClinicPicker
 *
 * One ezyVet database can hold several clinics (separation "divisions"), but a HappyDoc account is
 * one clinic. This lists the database's clinics and saves which ones this account is
 * (`account_settings.ezyvet_clinic_ids`). Nothing checked = every clinic syncs, the pre-existing
 * behavior. Needs the ezyVet API credentials already uploaded: without them the server answers
 * 409 and there is nothing to list.
 */
export default function EzyvetClinicPicker() {
    const { accountData } = useAccountContext();
    const queryClient = useQueryClient();
    const queryKey = ezyvetClinicsQueryKey(accountData?.id);

    const { data, isLoading, error: loadError } = useQuery({ queryKey, queryFn: fetchEzyvetClinics, retry: false });

    const [checked, setChecked] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    // Seed (and re-seed after a save updates the cache) from the server's selection.
    useEffect(() => {
        if (data) setChecked(new Set(data.selected));
    }, [data]);

    const needsCredentials = errorResponse(loadError)?.status === 409;
    const selected = data?.selected ?? [];
    const isUnchanged = checked.size === selected.length && selected.every((id) => checked.has(id));

    const toggle = (id: string) => {
        setChecked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setSaved(false);
        setError(null);
    };

    const handleSave = async () => {
        setIsSaving(true);
        setError(null);
        setSaved(false);
        try {
            const result = await saveEzyvetClinics(
                // Keep the server's clinic order rather than click order.
                (data?.clinics ?? []).map((c) => c.id).filter((id) => checked.has(id)),
            );
            queryClient.setQueryData(queryKey, (prev: typeof data) =>
                prev ? { ...prev, selected: result.selected } : prev,
            );
            setSaved(true);
        } catch (err) {
            Sentry.captureException(err);
            let message = "Failed to save your ezyVet clinics. Please try again or contact support.";
            const res = errorResponse(err);
            if (res) {
                try {
                    const body = await res.json();
                    if (body?.error) message = body.error;
                } catch {
                    // Non-JSON error body — keep the generic message.
                }
            }
            setError(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 flex flex-col gap-3">
            <div>
                <p className="text-sm font-medium text-gray-700">Your clinic(s) in ezyVet</p>
                <p className="text-xs text-gray-500 mt-1">
                    An ezyVet database can hold several clinics. Only the clinics checked here sync to this HappyDoc
                    account.
                </p>
            </div>

            {isLoading && <p className="text-xs text-gray-500">Loading clinics…</p>}

            {needsCredentials && <p className="text-sm text-gray-600">Connect ezyVet API credentials first</p>}

            {loadError && !needsCredentials && (
                <ErrorBox msg="Couldn't load your ezyVet clinics. Please try again or contact support." />
            )}

            {data && (
                <>
                    <div className="flex flex-col gap-2">
                        {data.clinics.map((clinic) => (
                            <label key={clinic.id} className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={checked.has(clinic.id)}
                                    onChange={() => toggle(clinic.id)}
                                />
                                {clinic.name}
                            </label>
                        ))}
                    </div>

                    {checked.size === 0 && (
                        <p className="text-xs text-gray-500">All clinics in this ezyVet database are synced</p>
                    )}

                    <div>
                        <button
                            type="button"
                            className="primary-button"
                            onClick={handleSave}
                            disabled={isSaving || isUnchanged}
                        >
                            {isSaving ? "Saving…" : "Save"}
                        </button>
                    </div>
                </>
            )}

            {saved && !isSaving && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                    <CheckCircleIcon className="h-4 w-4 shrink-0" />
                    <span>Saved. Run Sync from PIMS to update your doctors.</span>
                </div>
            )}

            {error && <ErrorBox msg={error} />}
        </div>
    );
}
